/**
 * The recruiter's home screen.
 *
 * This is the answer to the spreadsheet's biggest failing: 456 "call back" and
 * 141 "hold" promises that nothing ever resurfaced. Recall work leads, because
 * a followup nobody sees is the same as no followup at all.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Clock,
  Loader2,
  Phone,
  PhoneCall,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { formatPhone, telHref } from '../lib/phone';
import ShareLinks from '../components/ShareLinks';
import ShiftBar from '../components/ShiftBar';
import type { Followup, MyDay } from '../types';

export default function MyDayPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<MyDay>({
    queryKey: ['my-day'],
    queryFn: async () => (await client.get('/api/v1/my-day')).data,
  });

  const complete = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (
        await client.post(`/api/v1/followups/${id}/complete`, {
          outcomeNote: note ?? null,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-day'] });
      toast.success('Followup closed');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <ShiftBar />

      {/* Between the shift and the counts: the shortcuts are a start-of-day
          thing, and they are not a figure about today's work. */}
      <ShareLinks />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat
          label="Recall"
          value={data.overdueCount}
          icon={AlertCircle}
          tone={data.overdueCount > 0 ? 'danger' : 'neutral'}
        />
        <Stat label="Due today" value={data.dueTodayCount} icon={CalendarClock} />
        <Stat label="Calls today" value={data.callsToday} icon={PhoneCall} />
        <Stat label="Submissions today" value={data.submissionsToday} icon={Send} />
      </div>

      <FollowupSection
        title="Recall"
        description="These were promised and have slipped past their date."
        items={data.overdue}
        tone="danger"
        onComplete={(id, note) => complete.mutate({ id, note })}
        pending={complete.isPending}
      />

      <FollowupSection
        title="Due Today"
        items={data.dueToday}
        onComplete={(id, note) => complete.mutate({ id, note })}
        pending={complete.isPending}
      />

      <FollowupSection
        title="Coming Up"
        items={data.upcoming}
        collapsedByDefault
        onComplete={(id, note) => complete.mutate({ id, note })}
        pending={complete.isPending}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  icon: typeof Clock;
  tone?: 'neutral' | 'danger';
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{label}</p>
        <Icon
          className={`h-4 w-4 ${tone === 'danger' ? 'text-red-500' : 'text-gray-400'}`}
        />
      </div>
      <p
        className={`mt-2 text-2xl font-semibold ${
          tone === 'danger' && value > 0 ? 'text-red-600' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function FollowupSection({
  title,
  description,
  items,
  tone = 'neutral',
  collapsedByDefault = false,
  onComplete,
  pending,
}: {
  title: string;
  description?: string;
  items: Followup[];
  tone?: 'neutral' | 'danger';
  collapsedByDefault?: boolean;
  onComplete: (id: string, note?: string) => void;
  pending: boolean;
}) {
  const [expanded, setExpanded] = useState(!collapsedByDefault);

  if (items.length === 0) {
    return (
      <section>
        <h2 className="mb-2 text-sm font-semibold text-gray-900">{title}</h2>
        <div className="card flex items-center gap-2 text-sm text-gray-500">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Nothing here.
        </div>
      </section>
    );
  }

  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mb-2 flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            {title}{' '}
            <span
              className={`badge ml-1 ${
                tone === 'danger'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {items.length}
            </span>
          </h2>
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
        </div>
        <span className="text-xs text-gray-400">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
          {items.map((item) => (
            <FollowupRow
              key={item.id}
              item={item}
              onComplete={onComplete}
              pending={pending}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function FollowupRow({
  item,
  onComplete,
  pending,
}: {
  item: Followup;
  onComplete: (id: string, note?: string) => void;
  pending: boolean;
}) {
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  return (
    <li className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-gray-900">
              {item.candidateName ?? 'Unknown candidate'}
            </p>
            {item.candidatePhone && (
              <a
                href={`tel:${telHref(item.candidatePhone)}`}
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-indigo-600 hover:underline"
              >
                <Phone className="h-3.5 w-3.5" />
                {formatPhone(item.candidatePhone)}
              </a>
            )}
            {item.isOverdue && (
              <span className="badge bg-red-100 text-red-700">Recall</span>
            )}
            {item.autoCreated && (
              <span className="badge bg-gray-100 text-gray-600" title="Created automatically from a call outcome">
                auto
              </span>
            )}
          </div>

          {item.note && (
            <p className="mt-1 line-clamp-2 text-sm text-gray-600">{item.note}</p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Due {new Date(item.dueAt).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 flex gap-2">
          <input
            spellCheck={true}
            className="input"
            placeholder="What happened? (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
          <button
            type="button"
            className="btn-primary shrink-0"
            disabled={pending}
            onClick={() => {
              onComplete(item.id, note.trim() || undefined);
              setOpen(false);
              setNote('');
            }}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Done'}
          </button>
        </div>
      )}
    </li>
  );
}
